import { Table } from "common/Table/Table"
import { ReceiptPaidStatusControl } from "components/ReceiptPaidStatusControl"
import { EvoFiltersBar } from "components/evo/EvoFiltersBar"
import { EvoGroupDateCell } from "components/evo/EvoGroupDateCell"
import { EvoFieldLabel, EvoSelect, EvoStatTile } from "components/evo"
import { usePersistedFilters } from "hooks/usePersistedFilters"
import { useCallback, useEffect, useState } from "react"
import UserService from "services/user.service"
import {
  exportToCSV,
  formatDate,
  type ExportColumnDef,
  parseExportNumber,
} from "utils/funcs"
import { useNotifications } from "hooks/useNotification"
import { useSelector } from "react-redux"
import { userSelector } from "appRedux/selectors/user"
import { hasPermission } from "utils/permissions"
import classNames from "classnames"
import {
  approvedAmountColorClass,
  buildEvoQueryParams,
  formatMoney,
  isSubmittedRecord,
} from "utils/evoTripsUi"

const service = new UserService()

const formatCurrency = (value: number) =>
  typeof value === "number" && !isNaN(value) ? `$${value.toFixed(2)}` : "$0.00"

const getRowKey = (row: any) =>
  row._id ?? `${row.badgeId}-${row.month}-${row.year}-${row.fullName ?? ""}`

const MoneyCell = ({ value }: { value: unknown }) => (
  <span className="font-mono tabular-nums text-gray-800">
    {formatMoney(value, "—")}
  </span>
)

const ApprovedAmountCell = ({ row }: { row: any }) => {
  const submitted = isSubmittedRecord(row)
  return (
    <span className={approvedAmountColorClass(submitted)}>
      {formatMoney(row.monthlyTotalToBePaid, "$0.00")}
    </span>
  )
}

async function updateGroupTripField(
  trips: any[],
  field: "submissionDate" | "dateOfReimbursement",
  value: string
) {
  await Promise.all(
    (trips || []).map((trip) =>
      service.updateMyTrip(String(trip._id), { [field]: value || null })
    )
  )
}

async function updateGroupOperationField(
  trips: any[],
  field: "submissionDate" | "dateOfReimbursement",
  value: string
) {
  await Promise.all(
    (trips || []).map((trip) =>
      service.updateOperation(String(trip._id), { [field]: value || null })
    )
  )
}

const buildTravelColumns = (
  canEditDates: boolean,
  onDateChange: (
    row: any,
    field: "submissionDate" | "dateOfReimbursement",
    value: string
  ) => void,
  savingKey: string | null
) => [
  { key: "badgeId", dbColName: "badgeId", title: "Badge ID" },
  { key: "fullName", dbColName: "fullName", title: "Full Name", isSort: true },
  { key: "monthName", dbColName: "monthName", title: "Month" },
  {
    key: "dateSubmitted",
    title: "Date Submitted",
    render: (row: any) => {
      const rowKey = getRowKey(row)
      return (
        <EvoGroupDateCell
          value={row.dateSubmitted}
          disabled={!canEditDates || savingKey === `${rowKey}:submissionDate`}
          onChange={(value) => onDateChange(row, "submissionDate", value)}
        />
      )
    },
  },
  {
    key: "dateOfReimbursement",
    title: "Date of Reimbursement",
    render: (row: any) => {
      const rowKey = getRowKey(row)
      return (
        <EvoGroupDateCell
          value={row.dateOfReimbursement}
          disabled={!canEditDates || savingKey === `${rowKey}:dateOfReimbursement`}
          onChange={(value) => onDateChange(row, "dateOfReimbursement", value)}
        />
      )
    },
  },
  {
    key: "monthlyTotalToBePaid",
    title: "Approved Amount to Pay",
    render: (row: any) => <ApprovedAmountCell row={row} />,
  },
  {
    key: "monthlyTotalAmount",
    title: "Receipts Total",
    render: (row: any) => <MoneyCell value={row.monthlyTotalAmount} />,
  },
]

const buildOpsByMonthColumns = (
  canEditDates: boolean,
  onDateChange: (
    row: any,
    field: "submissionDate" | "dateOfReimbursement",
    value: string
  ) => void,
  savingKey: string | null
) => [
  { key: "fullName", dbColName: "fullName", title: "Full Name", isSort: true },
  { key: "monthName", dbColName: "monthName", title: "Month" },
  {
    key: "dateSubmitted",
    title: "Date Submitted",
    render: (row: any) => {
      const rowKey = getRowKey(row)
      return (
        <EvoGroupDateCell
          value={row.dateSubmitted}
          disabled={!canEditDates || savingKey === `${rowKey}:submissionDate`}
          onChange={(value) => onDateChange(row, "submissionDate", value)}
        />
      )
    },
  },
  {
    key: "dateOfReimbursement",
    title: "Date of Reimbursement",
    render: (row: any) => {
      const rowKey = getRowKey(row)
      return (
        <EvoGroupDateCell
          value={row.dateOfReimbursement}
          disabled={!canEditDates || savingKey === `${rowKey}:dateOfReimbursement`}
          onChange={(value) => onDateChange(row, "dateOfReimbursement", value)}
        />
      )
    },
  },
  {
    key: "monthlyTotalToBePaid",
    title: "Approved Amount to Pay",
    render: (row: any) => <ApprovedAmountCell row={row} />,
  },
  {
    key: "monthlyTotalAmount",
    title: "Receipts Total",
    render: (row: any) => <MoneyCell value={row.monthlyTotalAmount} />,
  },
]

type ExpandedProps = {
  row: any
  variant: "travel" | "operations"
  canEditPaid: boolean
  updatingKey: string | null
  onPaidChange: (
    parentId: string,
    expenseId: string,
    status: "Paid" | "Not-Paid"
  ) => void
}

const ExpandedExpenses = ({
  row,
  variant,
  canEditPaid,
  updatingKey,
  onPaidChange,
}: ExpandedProps) => {
  const trips = row.trips ?? []
  const source: "myTrips" | "operations" =
    variant === "operations" ? "operations" : "myTrips"

  const hasAnyExpenses = trips.some(
    (t: any) => Array.isArray(t.expenses) && t.expenses.length > 0
  )

  if (trips.length === 0) {
    return (
      <div className="flex items-center gap-2 py-3 text-sm text-gray-400">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
          <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        </svg>
        No records found
      </div>
    )
  }

  if (!hasAnyExpenses) {
    return (
      <div className="py-3 text-sm text-gray-400 flex items-center gap-2">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 shrink-0">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 14.25l6-6m4.5-3.493V21.75l-3.75-1.5-3.75 1.5-3.75-1.5-3.75 1.5V4.757c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0c1.1.128 1.907 1.077 1.907 2.185Z" />
        </svg>
        No expense receipts recorded for these entries.
      </div>
    )
  }

  return (
    <div className="py-2 pr-2 max-w-6xl">
      {/* Section label */}
      <div className="flex items-center gap-2 mb-3">
        <div className="w-1 h-4 rounded-full bg-primary" />
        <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
          {variant === "operations" ? "Receipts by Operation Record" : "Expenses by Trip"}
        </span>
      </div>

      <div className="space-y-3">
        {trips.map((trip: any) => {
          const expenses = Array.isArray(trip.expenses) ? trip.expenses : []
          if (expenses.length === 0) return null

          const title =
            variant === "operations"
              ? trip.monthName ?? "Operation"
              : trip.name ?? "Trip"

          const totalAmount = trip.totalAmount != null
            ? formatCurrency(parseFloat(trip.totalAmount))
            : null
          const totalToBePaid = trip.totalToBePaid != null && variant === "travel"
            ? formatCurrency(parseFloat(trip.totalToBePaid))
            : null

          return (
            <div
              key={trip._id}
              className="border border-gray-200 rounded-xl overflow-hidden shadow-card"
            >
              {/* Group header */}
              <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center gap-2.5">
                  <span className="w-2 h-2 rounded-full bg-primary/60 shrink-0" />
                  <span className="text-sm font-semibold text-gray-700">
                    {title}
                  </span>
                  {trip.startDate && trip.endDate && variant === "travel" && (
                    <span className="text-xs text-gray-400 font-normal">
                      {formatDate(trip.startDate)} – {formatDate(trip.endDate)}
                    </span>
                  )}
                  <span className="text-[10px] text-gray-400 bg-gray-100 rounded-full px-2 py-0.5 font-medium">
                    {expenses.length} receipt{expenses.length !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {totalAmount && (
                    <span className="inline-flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-2.5 py-1 text-xs font-mono font-semibold text-gray-700 shadow-sm">
                      <span className="text-gray-400 font-sans font-normal">Total</span>
                      {totalAmount}
                    </span>
                  )}
                  {totalToBePaid && (
                    <span className="inline-flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-2.5 py-1 text-xs font-mono font-semibold text-gray-700 shadow-sm">
                      <span className="text-gray-400 font-sans font-normal">To pay</span>
                      {totalToBePaid}
                    </span>
                  )}
                </div>
              </div>

              {/* Expense rows table */}
              <table className="w-full table-fixed">
                <thead>
                  <tr className="bg-white border-b border-gray-100">
                    <th className="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-widest text-gray-400 w-28">
                      Amount
                    </th>
                    <th className="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                      {variant === "operations" ? "Vendor / Notes" : "Description"}
                    </th>
                    <th className="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                      {variant === "operations" ? "City" : "Expense Type"}
                    </th>
                    <th className="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-widest text-gray-400 w-32">
                      Date
                    </th>
                    <th className="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-widest text-gray-400 w-44">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {expenses.map((exp: any, rowIdx: number) => {
                    const expenseId = exp._id?.toString?.() ?? String(exp._id)
                    const parentId = trip._id?.toString?.() ?? String(trip._id)
                    const uk = `${source}:${parentId}:${expenseId}`
                    const paidRaw =
                      exp.receiptPaidStatus === "Paid" ||
                      exp.receiptPaidStatus === "Not-Paid"
                        ? exp.receiptPaidStatus
                        : "Not-Paid"
                    const rowDate =
                      variant === "operations"
                        ? exp.transactionDate
                          ? formatDate(exp.transactionDate)
                          : exp.createdAt
                            ? formatDate(exp.createdAt)
                            : "—"
                        : exp.transactionDate
                          ? formatDate(exp.transactionDate)
                          : exp.date
                            ? formatDate(exp.date)
                            : "—"

                    return (
                      <tr
                        key={expenseId}
                        className={classNames(
                          "transition-colors duration-100 hover:bg-primary-50/20",
                          rowIdx % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                        )}
                      >
                        {/* Amount */}
                        <td className="px-4 py-2.5 w-28">
                          <span className="font-mono font-semibold tabular-nums text-sm text-gray-800">
                            {formatCurrency(parseFloat(exp.amount))}
                          </span>
                        </td>
                        {/* Vendor / Description */}
                        <td className="px-4 py-2.5 text-sm text-gray-600 truncate max-w-0">
                          <span className="truncate block">
                            {(variant === "operations"
                              ? exp.vendorName ?? exp.description
                              : exp.description) || (
                              <span className="text-gray-300">—</span>
                            )}
                          </span>
                        </td>
                        {/* City / Type */}
                        <td className="px-4 py-2.5 text-sm text-gray-500">
                          {(variant === "operations" ? exp.city : exp.expenseType) || (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        {/* Date */}
                        <td className="px-4 py-2.5 w-32 text-sm text-gray-500 tabular-nums">
                          {rowDate}
                        </td>
                        {/* Status */}
                        <td className="px-4 py-2.5 w-44">
                          <ReceiptPaidStatusControl
                            value={paidRaw}
                            saving={updatingKey === uk}
                            disabled={!canEditPaid}
                            onChange={
                              canEditPaid
                                ? (next) => onPaidChange(parentId, expenseId, next)
                                : undefined
                            }
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/** Spreadsheet export: scalar columns only (no nested trips / JSON blobs). */
const TRAVEL_GROUP_EXPORT_COLUMNS: ExportColumnDef[] = [
  { dbColName: "badgeId", title: "Badge ID" },
  { dbColName: "fullName", title: "Full Name" },
  { dbColName: "monthName", title: "Month" },
  { dbColName: "dateSubmitted", title: "Date Submitted" },
  { dbColName: "dateOfReimbursement", title: "Date of Reimbursement" },
  { dbColName: "monthlyTotalToBePaid", title: "Approved Amount to Pay", decimals: 2 },
  { dbColName: "monthlyTotalAmount", title: "Receipts Total", decimals: 2 },
]

const OPS_GROUP_EXPORT_COLUMNS: ExportColumnDef[] = [
  { dbColName: "fullName", title: "Full Name" },
  { dbColName: "monthName", title: "Month" },
  { dbColName: "dateSubmitted", title: "Date Submitted" },
  { dbColName: "dateOfReimbursement", title: "Date of Reimbursement" },
  { dbColName: "monthlyTotalToBePaid", title: "Approved Amount to Pay", decimals: 2 },
  { dbColName: "monthlyTotalAmount", title: "Receipts Total", decimals: 2 },
]

function mapGroupByMonthRowForExport(r: any) {
  const num = (v: unknown) => parseExportNumber(v) ?? 0
  return {
    badgeId: String(r.badgeId ?? ""),
    fullName: String(r.fullName ?? ""),
    monthName: String(r.monthName ?? ""),
    dateSubmitted: r.dateSubmitted ? formatDate(r.dateSubmitted) : "",
    dateOfReimbursement: r.dateOfReimbursement ? formatDate(r.dateOfReimbursement) : "",
    monthlyTotalToBePaid: num(r.monthlyTotalToBePaid),
    monthlyTotalAmount: num(r.monthlyTotalAmount),
  }
}

// ── Travel by Month ───────────────────────────────────────────────────
export const TravelByMonth = () => {
  const [expandedTravel, setExpandedTravel] = useState<string[]>([])
  const [loadingTravel, setLoadingTravel] = useState(false)
  const [dataTravel, setDataTravel] = useState<any[]>([])
  const [totalTravel, setTotalTravel] = useState(0)
  const [paginationTravel, setPaginationTravel] = useState({
    pageNumber: 1,
    pageSize: 100,
  })
  const [updatingKey, setUpdatingKey] = useState<string | null>(null)
  const [exportingTravel, setExportingTravel] = useState(false)
  const [savingDateKey, setSavingDateKey] = useState<string | null>(null)
  const { filters, setFilters, clearFilters } = usePersistedFilters("travel-by-month")

  const { successMessage, errorMessage } = useNotifications()
  const currentUser = useSelector(userSelector)
  const canEditPaid = hasPermission(currentUser, "receiptTransactions", "edit")
  const canEditDates = hasPermission(currentUser, "myTrips", "edit")

  const fetchTravel = useCallback(() => {
    setLoadingTravel(true)
    service
      .getGroupByMonth(buildEvoQueryParams(paginationTravel, filters))
      .then((resp) => {
        setDataTravel(resp.data.data)
        setTotalTravel(resp.data.totalDocument)
      })
      .catch((error) => console.log(error))
      .finally(() => setLoadingTravel(false))
  }, [paginationTravel, filters.fullName, filters.month, filters.year])

  useEffect(() => {
    fetchTravel()
  }, [fetchTravel])

  useEffect(() => {
    setPaginationTravel((p) => ({ ...p, pageNumber: 1 }))
  }, [filters.fullName, filters.month, filters.year])

  const handleTravelDateChange = async (
    row: any,
    field: "submissionDate" | "dateOfReimbursement",
    value: string
  ) => {
    const rowKey = getRowKey(row)
    const uk = `${rowKey}:${field}`
    setSavingDateKey(uk)
    try {
      await updateGroupTripField(row.trips || [], field, value)
      successMessage("Date updated")
      fetchTravel()
    } catch (e: any) {
      errorMessage(e?.response?.data?.message || "Could not update date")
    } finally {
      setSavingDateKey(null)
    }
  }

  const travelColumns = buildTravelColumns(
    canEditDates,
    handleTravelDateChange,
    savingDateKey
  )

  const handlePaidChange = async (
    parentId: string,
    expenseId: string,
    status: "Paid" | "Not-Paid"
  ) => {
    const uk = `myTrips:${parentId}:${expenseId}`
    setUpdatingKey(uk)
    try {
      await service.updateReceiptPaidStatus({
        source: "myTrips",
        parentId,
        expenseId,
        receiptPaidStatus: status,
      })
      successMessage("Payment status updated")
      fetchTravel()
    } catch (e: any) {
      errorMessage(e?.response?.data?.message || "Could not update payment status")
    } finally {
      setUpdatingKey(null)
    }
  }

  const handleExportTravel = async () => {
    setExportingTravel(true)
    try {
      const resp = await service.getGroupByMonth({
        ...buildEvoQueryParams(paginationTravel, filters),
        isDownload: "true",
      })
      const rows = (resp.data.data ?? []).map(mapGroupByMonthRowForExport)
      exportToCSV(rows, "travel-by-month", {
        columns: TRAVEL_GROUP_EXPORT_COLUMNS,
      })
    } catch (e) {
      console.error(e)
    } finally {
      setExportingTravel(false)
    }
  }

  return (
    <div>
      <div className="mb-4 grid max-w-xs grid-cols-1 gap-3 sm:grid-cols-2">
        <EvoStatTile
          label="Groups"
          value={loadingTravel ? "—" : totalTravel}
          hint="Travel by month"
        />
      </div>
      <Table
        data={dataTravel}
        loadingData={loadingTravel}
        setData={setDataTravel}
        totalItems={totalTravel}
        setPagination={setPaginationTravel}
        pagination={paginationTravel}
        columns={travelColumns}
        exportButton={true}
        handleExport={handleExportTravel}
        exporting={exportingTravel}
        showSearch={false}
        filters={
          <EvoFiltersBar
            filters={filters}
            onChange={(patch) => setFilters(patch)}
            onClear={clearFilters}
          />
        }
        expandable={{
          rowKey: getRowKey,
          expandedRowRender: (row) => (
            <ExpandedExpenses
              row={row}
              variant="travel"
              canEditPaid={canEditPaid}
              updatingKey={updatingKey}
              onPaidChange={handlePaidChange}
            />
          ),
        }}
        expandedRowKeys={expandedTravel}
        onExpandedRowsChange={setExpandedTravel}
      />
    </div>
  )
}

// ── Operations by Month ───────────────────────────────────────────────
export const OperationsByMonth = () => {
  const [expandedOps, setExpandedOps] = useState<string[]>([])
  const [paidFilter, setPaidFilter] = useState<"all" | "paid" | "unpaid">("all")
  const [loadingOps, setLoadingOps] = useState(false)
  const [dataOps, setDataOps] = useState<any[]>([])
  const [totalOps, setTotalOps] = useState(0)
  const [paginationOps, setPaginationOps] = useState({
    pageNumber: 1,
    pageSize: 100,
  })
  const [updatingKey, setUpdatingKey] = useState<string | null>(null)
  const [exportingOps, setExportingOps] = useState(false)
  const [savingDateKey, setSavingDateKey] = useState<string | null>(null)
  const { filters, setFilters, clearFilters } = usePersistedFilters("operations-by-month")

  const { successMessage, errorMessage } = useNotifications()
  const currentUser = useSelector(userSelector)
  const canEditPaid = hasPermission(currentUser, "receiptTransactions", "edit")
  const canEditDates = hasPermission(currentUser, "myTrips", "edit")

  const fetchOps = useCallback(() => {
    setLoadingOps(true)
    service
      .getGroupByMonthOperations(buildEvoQueryParams(paginationOps, filters), paidFilter)
      .then((resp) => {
        setDataOps(resp.data.data)
        setTotalOps(resp.data.totalDocument)
      })
      .catch((error) => console.log(error))
      .finally(() => setLoadingOps(false))
  }, [paginationOps, paidFilter, filters.fullName, filters.month, filters.year])

  useEffect(() => {
    fetchOps()
  }, [fetchOps])

  useEffect(() => {
    setPaginationOps((p) => ({ ...p, pageNumber: 1 }))
  }, [paidFilter, filters.fullName, filters.month, filters.year])

  const handleOpsDateChange = async (
    row: any,
    field: "submissionDate" | "dateOfReimbursement",
    value: string
  ) => {
    const rowKey = getRowKey(row)
    const uk = `${rowKey}:${field}`
    setSavingDateKey(uk)
    try {
      await updateGroupOperationField(row.trips || [], field, value)
      successMessage("Date updated")
      fetchOps()
    } catch (e: any) {
      errorMessage(e?.response?.data?.message || "Could not update date")
    } finally {
      setSavingDateKey(null)
    }
  }

  const opsColumns = buildOpsByMonthColumns(
    canEditDates,
    handleOpsDateChange,
    savingDateKey
  )

  const handlePaidChange = async (
    parentId: string,
    expenseId: string,
    status: "Paid" | "Not-Paid"
  ) => {
    const uk = `operations:${parentId}:${expenseId}`
    setUpdatingKey(uk)
    try {
      await service.updateReceiptPaidStatus({
        source: "operations",
        parentId,
        expenseId,
        receiptPaidStatus: status,
      })
      successMessage("Payment status updated")
      fetchOps()
    } catch (e: any) {
      errorMessage(e?.response?.data?.message || "Could not update payment status")
    } finally {
      setUpdatingKey(null)
    }
  }

  const handleExportOps = async () => {
    setExportingOps(true)
    try {
      const resp = await service.getGroupByMonthOperations(
        {
          ...buildEvoQueryParams(paginationOps, filters),
          isDownload: "true",
        },
        paidFilter
      )
      const rows = (resp.data.data ?? []).map(mapGroupByMonthRowForExport)
      exportToCSV(rows, `operations-by-month-${paidFilter}`, {
        columns: OPS_GROUP_EXPORT_COLUMNS,
      })
    } catch (e) {
      console.error(e)
    } finally {
      setExportingOps(false)
    }
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div className="grid max-w-xs grid-cols-1 gap-3 sm:grid-cols-2">
          <EvoStatTile
            label="Groups"
            value={loadingOps ? "—" : totalOps}
            hint="Operations by month"
          />
        </div>
        <div className="flex min-w-[10rem] flex-col gap-1">
          <EvoFieldLabel>Payment status</EvoFieldLabel>
          <EvoSelect
            value={paidFilter}
            onChange={(e) =>
              setPaidFilter(e.target.value as "all" | "paid" | "unpaid")
            }
            className="min-w-[10rem]"
          >
            <option value="all">All</option>
            <option value="unpaid">Unpaid only</option>
            <option value="paid">Fully paid</option>
          </EvoSelect>
        </div>
      </div>

      <Table
        data={dataOps}
        loadingData={loadingOps}
        setData={setDataOps}
        totalItems={totalOps}
        setPagination={setPaginationOps}
        pagination={paginationOps}
        columns={opsColumns}
        exportButton={true}
        handleExport={handleExportOps}
        exporting={exportingOps}
        showSearch={false}
        filters={
          <EvoFiltersBar
            filters={filters}
            onChange={(patch) => setFilters(patch)}
            onClear={clearFilters}
          />
        }
        expandable={{
          rowKey: getRowKey,
          expandedRowRender: (row) => (
            <ExpandedExpenses
              row={row}
              variant="operations"
              canEditPaid={canEditPaid}
              updatingKey={updatingKey}
              onPaidChange={handlePaidChange}
            />
          ),
        }}
        expandedRowKeys={expandedOps}
        onExpandedRowsChange={setExpandedOps}
      />
    </div>
  )
}
